import { BatteryStatus, SignalQuality } from '@/api_generated/api'

// Single source of truth for how the backend BatteryStatus / SignalQuality enums
// are labelled and coloured. Shared by the Plant Status donut charts and the
// Sensor Overview filter so the two stay 1:1 consistent.
export interface HealthFilterOption<T> {
    value: T
    label: string
    color: string
}

export const HEALTH_GOOD_COLOR = '#7cc278'
export const HEALTH_WARN_COLOR = '#e89959'
export const HEALTH_BAD_COLOR = '#c6002d'
export const HEALTH_NO_INFO_COLOR = '#aeaeae'

export const BATTERY_STATUS_OPTIONS: HealthFilterOption<BatteryStatus>[] = [
    { value: BatteryStatus.Good, label: '20-100%', color: HEALTH_GOOD_COLOR },
    { value: BatteryStatus.Low, label: '5-20%', color: HEALTH_WARN_COLOR },
    { value: BatteryStatus.Empty, label: '0-5%', color: HEALTH_BAD_COLOR },
    { value: BatteryStatus.NotAvailable, label: 'No info', color: HEALTH_NO_INFO_COLOR },
]

export const SIGNAL_QUALITY_OPTIONS: HealthFilterOption<SignalQuality>[] = [
    { value: SignalQuality.Good, label: 'Good', color: HEALTH_GOOD_COLOR },
    { value: SignalQuality.OK, label: 'OK', color: HEALTH_WARN_COLOR },
    { value: SignalQuality.Bad, label: 'Bad', color: HEALTH_BAD_COLOR },
    { value: SignalQuality.NotAvailable, label: 'No info', color: HEALTH_NO_INFO_COLOR },
]

// The backend enums are serialized inconsistently for the "not available" case
// (System.Text.Json emits the C# member name "NotAvailable"; Newtonsoft honours
// [EnumMember] "NoInfo"). Normalise any incoming value to the canonical frontend enum so
// the icon, label, filter and donut all agree regardless of which serializer produced it.
export const normalizeSignalQuality = (raw?: string | null): SignalQuality => {
    switch (raw) {
        case SignalQuality.Good:
            return SignalQuality.Good
        case SignalQuality.OK:
            return SignalQuality.OK
        case SignalQuality.Bad:
            return SignalQuality.Bad
        default:
            return SignalQuality.NotAvailable
    }
}

export const normalizeBatteryStatus = (raw?: string | null): BatteryStatus => {
    switch (raw) {
        case BatteryStatus.Good:
            return BatteryStatus.Good
        case BatteryStatus.Low:
            return BatteryStatus.Low
        case BatteryStatus.Empty:
            return BatteryStatus.Empty
        default:
            return BatteryStatus.NotAvailable
    }
}

const SIGNAL_QUALITY_LABELS: Record<SignalQuality, string> = {
    [SignalQuality.Good]: 'Good',
    [SignalQuality.OK]: 'OK',
    [SignalQuality.Bad]: 'Bad',
    [SignalQuality.NotAvailable]: '-',
}

// Card/detail label for signal quality: Good / OK / Bad, or "-" when unknown.
export const formatSignalQualityDisplay = (quality?: SignalQuality | null): string =>
    SIGNAL_QUALITY_LABELS[normalizeSignalQuality(quality)]

// Battery percentage for display. When the value is estimated from voltage (older/
// proprietary sensors) the percentage is an approximate upper bound, so it is prefixed
// with "< " to signal that it is a ceiling rather than an exact reading.
export const formatBatteryDisplay = (
    battery?: { value?: number | null } | null,
    estimated?: boolean | null
): string => {
    if (battery?.value == null) {
        return '-'
    }
    return estimated ? `< ${battery.value} %` : `${battery.value} %`
}
